/**
 * ScoreBox — the editorial Dark Navy panel anchoring the standfirst.
 *
 * Visual reference: 04-hybrid-recommended.html .score-box block.
 * Scoped to dark via `data-score-box` so it stays navy in light theme,
 * matching the mockup's editorial accent-block treatment.
 */

import { useTheme } from '@/context/ThemeContext'

interface ComponentBar {
  label: string
  /** Display weight string, e.g. "20%" — shown next to the label. */
  weight: string
  /** Score value (0-100). */
  value: number
  /** Tailwind utility for the fill — only `primary-500`, `secondary-blue-300`,
   *  `secondary-sand-300` are used (matches the mockup palette). */
  fillClass: string
}

interface Props {
  /** Composite Guardian Score (0-100). Pass `null` to show "—". */
  score: number | null
  /** Verdict line, italic, in quotes. Six words or fewer. */
  verdict: string
  /** Score change vs N days ago. */
  delta: { value: number; days: number }
  /** Component breakdown — drives the four inline weighted bars. */
  components: ComponentBar[]
  isLoading?: boolean
  /** Eyebrow label above the big number. Defaults to "Guardian Score" so the
   *  Command Center keeps its composite framing. Per-pillar pages override
   *  this to name the pillar score they're actually showing (e.g. "Best
   *  Practice Score") so a number that swings only with that pillar doesn't
   *  read as the cross-pillar Guardian composite. */
  label?: string
}

export default function ScoreBox({
  score,
  verdict,
  delta,
  components,
  isLoading = false,
  label = 'Guardian Score',
}: Props) {
  // Subscribe to theme so children re-render on toggle even though our
  // surface tokens are pinned via [data-score-box].
  useTheme()

  const deltaSign = delta.value >= 0 ? '▲' : '▼'
  const deltaAbs = Math.abs(delta.value).toFixed(1)

  return (
    <div
      data-score-box
      className="
        relative isolate flex flex-col justify-between overflow-hidden
        rounded-md bg-dark-800 p-8 lg:p-9
        text-slate-100
      "
      style={{ minHeight: 360 }}
    >
      {/* Soft Green radial flare in the upper-right — preserves the mockup's
          atmospheric glow without leaning on a Tailwind shadow utility. */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-20 -right-20 h-72 w-72 rounded-full opacity-[0.18]"
        style={{
          background:
            'radial-gradient(circle, rgb(var(--color-primary-500)) 0%, transparent 65%)',
        }}
      />
      {/* Subtle vertical pin-stripe overlay for editorial texture. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            'repeating-linear-gradient(90deg, transparent 0, transparent 49px, rgba(255,255,255,0.025) 50px)',
        }}
      />

      <div className="relative">
        <div className="text-[10px] font-bold uppercase tracking-[0.28em] text-primary-500">
          {label}
        </div>
        <div className="mt-3 font-display font-extrabold leading-[0.85] tracking-[-0.045em] text-white">
          <span className="text-[96px] xl:text-[110px]">
            {isLoading ? '—' : score == null ? '—' : score}
          </span>
          <span className="ml-1 text-[20px] xl:text-[22px] font-semibold text-white/45">/100</span>
        </div>
        <div className="mt-3 italic text-[14px] leading-[1.5] text-white/85">
          &ldquo;{verdict}&rdquo;
        </div>
        <div className="mt-5 inline-flex items-center gap-2 self-start rounded-full border border-primary-500/40 bg-primary-500/15 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-primary-500">
          <span aria-hidden>{deltaSign}</span>
          <span className="font-mono">
            {deltaSign === '▲' ? '+' : '-'}
            {deltaAbs} pts
          </span>
          <span className="text-primary-500/70">· last {delta.days} days</span>
        </div>
      </div>

      <div className="relative mt-6 border-t border-white/10 pt-5">
        <h4 className="mb-3 text-[9px] font-bold uppercase tracking-[0.24em] text-white/50">
          Composite
        </h4>
        <div className="flex flex-col gap-1">
          {components.map(c => (
            <ComponentRow key={c.label} {...c} />
          ))}
        </div>
        <p className="mt-3 italic text-[11px] leading-[1.5] text-white/55">
          Score weights each control by security impact &mdash; high-leverage controls move the score more than baseline hygiene.
        </p>
      </div>
    </div>
  )
}

function ComponentRow({ label, weight, value, fillClass }: ComponentBar) {
  return (
    <div className="grid grid-cols-[1fr_70px_30px] items-center gap-3 py-1">
      <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-white/85">
        {label}
        <span className="ml-1.5 text-[9px] font-medium text-white/40">{weight}</span>
      </span>
      <span className="h-[5px] overflow-hidden rounded-full bg-white/[0.08]">
        <i
          className={`block h-full rounded-full ${fillClass}`}
          style={{
            width: `${Math.max(0, Math.min(100, value))}%`,
            transformOrigin: 'left',
          }}
        />
      </span>
      <span className="text-right font-mono text-[12px] font-bold text-white tabular-nums">
        {value}
      </span>
    </div>
  )
}
